import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
// Perf 3g: TYPE-only — the SDK's 172KB of runtime code loads inside initDevice,
// after the voice-token fetch succeeds, and only for roles holding tab:comms.
// Every user used to download the softphone engine before the login form
// rendered; roles without the comms tab never download it at all now.
import type { Device, Call } from '@twilio/voice-sdk';
import { communicationsApi } from '../services/api';
import { api } from '../services/apiClient';
import { conversationsApi } from '../services/conversationsApi';
import { useInboxSocket, type InboxEvent } from '../hooks/useInboxSocket';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
// PHONE-A1 — the ring's state machine. Extracted so the WS/SDK ordering, the
// sid mismatch and the accept-before-invite race are unit-testable without the
// Twilio SDK or a socket. See its header for what each of those cost.
import {
  incomingCallReducer,
  EMPTY_RING_STATE,
  DEFAULT_RING_MS,
  type IncomingCallState,
  type IncomingCallAction,
  type IncomingCallBanner as IncomingCallBannerData,
  type RingIntent,
} from './incomingCallState';
// PHONE-A5 — the OS-level alert for a ring that arrives while the app is not
// the window being looked at. See its header for why this is the Notifications
// API and not web push.
import { useDesktopRingNotification } from '../components/phone/useDesktopRingNotification';
import { notificationsCenterApi } from '../services/notificationsCenterApi';

// Perf 3g: these two statically dragged 15 eager components/phone/** files
// into main.js. Lazy + fallback null — the banner self-hides until a call
// rings and the dialer until openDialer() runs, so nothing visible changes.
const IncomingCallBanner = React.lazy(() => import('../components/phone/IncomingCallBanner'));
const GlobalDialer = React.lazy(() => import('../components/phone/dialer/GlobalDialer'));

export type TwilioConnectionState = 'connected' | 'connecting' | 'disconnected' | 'error';

export interface TwilioTestConnectionResult {
  ok: boolean;
  twilioAccountSid?: string;
  accountName?: string;
  error?: string;
}

export interface FromNumberOption {
  phoneNumberId: number;
  phoneNumber: string;
  friendlyName: string | null;
  isDefault: boolean;
}

/**
 * PHONE-A1 — the banner shape now lives in `incomingCallState.ts` (it is the
 * reducer's, not the provider's). Re-exported under the historical name so
 * every existing consumer keeps compiling. `toUserId` and `ringExpiresAt`
 * became optional/derived because a DEVICE-announced ring knows neither: Twilio
 * tells us who is calling, not which of our users the server published to.
 */
export type IncomingCallData = IncomingCallBannerData;

// T4.1 — Contact context attached to an outbound call. Drives the active-call
// header (avatar/name) and the call-log row's contact/lead linkage.
export interface OutboundCallContext {
  contactId?: number;
  leadId?: number;
  clientCardId?: string;
  name?: string;
  phoneNumber?: string; // normalized E.164 form rendered in the header
  callId?: number; // comm_call_logs row id created at initiate time
  // PHONE-C3 — the deal this call belongs to, when the surface that started it
  // knows. It is known at the CLICK and nowhere afterwards: the contact may
  // sit on several open deals or none, so a call placed from a deal card can
  // only be attributed by carrying the id with it.
  dealId?: number;
}

// T4.1 — Options bag for startCall. opts.fromNumber wins over the user's default;
// autoOpenDialer defaults true (almost every caller wants the dialer to surface).
export interface StartCallOptions {
  fromNumber?: string;
  autoOpenDialer?: boolean;
}

// T4.4 — When PhoneNumberCallButton (or any future caller) wants to open the
// dialer without a contact attached, openDialer takes an optional seed.
export interface OpenDialerOptions {
  initialNumber?: string;
  contactId?: number;
  leadId?: number;
  contactName?: string;
  // Phase 8: when the dialer is opened from a phone action-task, the task
  // id rides along so the dialer can complete the task on call-end
  // (todosApi.fireAction → "placed phone call") / downgrade on failure.
  // Phone is human-only — there is NO auto-dial; placing the call IS the
  // completion. The DialerIntercom watches callState to fire the contract.
  todoId?: number;
  // PHONE-C3 — carried from the deal modal's contact-chip phone button through
  // the dialer to startCall. The deal id was ALREADY in hand at that click and
  // was thrown away one line later, which is why a call placed from a deal
  // could never be attached to it.
  dealId?: number;
}

interface TwilioContextType {
  device: Device | null;
  isReady: boolean;
  activeCall: Call | null;
  callState: 'idle' | 'connecting' | 'ringing' | 'open' | 'disconnected';
  isMuted: boolean;
  makeCall: (toNumber: string, contactId?: number, clientCardId?: string) => Promise<number | undefined>;
  hangUp: () => void;
  toggleMute: () => void;
  sendDtmf: (digit: string) => void;
  error: string | null;
  configured: boolean;
  connectionState: TwilioConnectionState;
  reconnect: () => Promise<void>;
  testConnection: () => Promise<TwilioTestConnectionResult>;
  availableFromNumbers: FromNumberOption[];
  defaultFromNumber: FromNumberOption | null;
  fromNumbersLoading: boolean;
  refreshAssignments: () => Promise<void>;
  incomingCall: IncomingCallData | null;
  acceptIncomingCall: () => void;
  declineIncomingCall: () => void;
  sendIncomingCallToVoicemail: () => void;
  // T4.1 — Canonical outbound-call entry point. Use this instead of makeCall
  // for any new call site; makeCall is kept for back-compat with existing
  // T2/T3-era consumers and now delegates to startCall internally.
  startCall: (phoneNumber: string, contactContext?: OutboundCallContext, opts?: StartCallOptions) => Promise<number | undefined>;
  outboundCallContext: OutboundCallContext | null;
  // T4 — Global dialer surface. PhoneNumberCallButton triggers this so the
  // dialer opens from anywhere in the app without each call site having to
  // wire its own SoftphoneDrawer.
  isDialerOpen: boolean;
  dialerSeed: OpenDialerOptions | null;
  openDialer: (opts?: OpenDialerOptions) => void;
  closeDialer: () => void;
  // T4b — when a call is placed with { autoOpenDialer: false } (e.g. the campaign
  // "call crusher" drives its own in-call UI), the global DialerIntercom would
  // otherwise still surface because isInCall is true. This flag keeps it hidden
  // for the duration of that call; openDialer() or a normal call clears it.
  suppressIntercom: boolean;
  // MOBILE-2E P3 — a surface that draws EVERY call itself (the phone shell's
  // call layer, mobile/v2/phone/MobileCallLayer) claims the calls while it is
  // mounted. While a claim is held, nothing opens the desktop dialer: accepting
  // an incoming call no longer pops the DialerIntercom over the phone app, a
  // call placed without { autoOpenDialer: false } does not open it either, and
  // the intercom stays suppressed. Returns the release. Desktop never claims,
  // so desktop behaviour is unchanged. Gated on the surface being MOUNTED,
  // never on the window's width.
  claimCallSurface: () => () => void;
  // MOBILE-2E P3 — the comm_call_logs id of the current call, or of the call
  // that just ended. Outbound: the initiate route's id; inbound: the by-sid
  // lookup made on Answer (incoming calls get their row at routing time).
  // It SURVIVES the hang-up (outboundCallContext is cleared on disconnect, and
  // the by-sid answer can land after a short call has already ended) so the
  // wrap-up can log the outcome; it is reset when the next call starts or is
  // answered.
  callLogId: number | null;
}

const TwilioContext = createContext<TwilioContextType>({
  device: null,
  isReady: false,
  activeCall: null,
  callState: 'idle',
  isMuted: false,
  makeCall: async () => undefined,
  hangUp: () => {},
  toggleMute: () => {},
  sendDtmf: () => {},
  error: null,
  configured: false,
  connectionState: 'disconnected',
  reconnect: async () => {},
  testConnection: async () => ({ ok: false, error: 'TwilioProvider not mounted' }),
  availableFromNumbers: [],
  defaultFromNumber: null,
  fromNumbersLoading: false,
  refreshAssignments: async () => {},
  incomingCall: null,
  acceptIncomingCall: () => {},
  declineIncomingCall: () => {},
  sendIncomingCallToVoicemail: () => {},
  startCall: async () => undefined,
  outboundCallContext: null,
  isDialerOpen: false,
  dialerSeed: null,
  openDialer: () => {},
  closeDialer: () => {},
  suppressIntercom: false,
  claimCallSurface: () => () => {},
  callLogId: null,
});

// ─── Perf Phase 3d — call duration ticks in its OWN context ────────────────
//
// setCallDuration fires once per second for the whole length of a connected
// call, and the provider value used to be a RAW OBJECT LITERAL — so every
// tick re-rendered all ~30 useTwilio() consumers (the boards, todos and the
// card modal among them), even though only three components display the
// timer. The main value is now memoised and the ticking number lives here;
// exactly CommsCallModal, ActiveCallHeader and CampaignInCallWorkspace
// subscribe. Everything else stops re-rendering during calls.
const CallDurationContext = createContext<number>(0);

/** Seconds the current call has been connected — ticks 1/sec while a call is
 *  open, 0 otherwise. Subscribing means re-rendering every second of every
 *  call; only components that RENDER the duration should use it (perf 3d). */
export function useCallDuration(): number {
  return useContext(CallDurationContext);
}

export const useTwilio = () => useContext(TwilioContext);

// T4.1 — Frontend-side E.164 normalizer. Mirrors the backend phonesService
// rule (T3a) — prepend '+' if missing, '+1' if both '+' and country code are
// missing. Used by startCall before hitting the API so the call-log row
// stores a canonical number.
function normalizeToE164(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

// T3b.2 — Structured Device + Call lifecycle logging. Prefix is greppable.
// Use console.info for non-error transitions, console.error for failures.
// Goal: when Michael (or anyone) opens DevTools during an inbound call, the
// exact sequence and timing of Device-side events is visible, which is what
// we need to diagnose the "first-call cold-start" report.
function logDeviceEvent(name: string, payload?: Record<string, unknown>, level: 'info' | 'error' = 'info') {
  const stamp = new Date().toISOString();
  const fn = level === 'error' ? console.error : console.info;
  if (payload) {
    fn(`[Twilio Device] ${name} at ${stamp}`, payload);
  } else {
    fn(`[Twilio Device] ${name} at ${stamp}`);
  }
}

function logCallEvent(name: string, callSid?: string, payload?: Record<string, unknown>) {
  const stamp = new Date().toISOString();
  const sidPart = callSid ? ` callSid=${callSid}` : '';
  if (payload) {
    console.info(`[Twilio Call] ${name} at ${stamp}${sidPart}`, payload);
  } else {
    console.info(`[Twilio Call] ${name} at ${stamp}${sidPart}`);
  }
}

export const TwilioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, hasTab } = useAuth();
  const toast = useToast();
  // V-18 — the same query App.tsx uses to pick the mobile shell. The mobile
  // Comms screen renders its own incoming-call banner, so the global portal one
  // below is desktop-only; without this both appeared, each with its own ring
  // tone. (DebugPanel and DevModeIndicator already repeat this literal for the
  // same reason: the constant would have to live in the frozen App.tsx.)
  const isMobileShell = useMediaQuery('(max-width:767.95px)');
  const [device, setDevice] = useState<Device | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [callState, setCallState] = useState<TwilioContextType['callState']>('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [connectionState, setConnectionState] = useState<TwilioConnectionState>('disconnected');
  const [availableFromNumbers, setAvailableFromNumbers] = useState<FromNumberOption[]>([]);
  const [fromNumbersLoading, setFromNumbersLoading] = useState(false);
  // T3.3 / PHONE-A1 — Incoming ring state.
  //
  // BOTH channels can now announce a ring: the Twilio Device's own 'incoming'
  // event (which means the call is genuinely on this browser) and the server's
  // `phone.incoming_call` WebSocket event (which adds the caller's NAME and the
  // parent CallSid). Whichever lands first raises the banner; the other MERGES
  // into it. Before PHONE-A1 only the socket could raise it, so a dropped
  // socket meant a silent phone with Twilio ringing an invitation nobody saw.
  //
  // All of the ordering, sid-matching and click-before-invite logic lives in
  // `incomingCallState.ts` as a pure reducer. The ref mirrors the state because
  // the SDK's event handlers are registered once, inside initDevice's closure,
  // and would otherwise read a stale banner forever.
  const [ringState, setRingState] = useState<IncomingCallState>(EMPTY_RING_STATE);
  const ringStateRef = useRef<IncomingCallState>(EMPTY_RING_STATE);
  const incomingCall = ringState.banner;
  const incomingCallObjRef = useRef<Call | null>(null);
  const incomingDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PHONE-A1 — set when a remembered Accept/Decline has to run the moment the
  // SDK invite arrives. Read by the 'incoming' handler; see applyRingIntentRef.
  const applyRingIntentRef = useRef<((intent: RingIntent, call: Call) => void) | null>(null);
  // T4 — Outbound call context + global dialer surface.
  const [outboundCallContext, setOutboundCallContext] = useState<OutboundCallContext | null>(null);
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const [dialerSeed, setDialerSeed] = useState<OpenDialerOptions | null>(null);
  // T4b — hides the global DialerIntercom while a call that owns its own in-call
  // surface (campaign call crusher) is live. Set by startCall when autoOpenDialer
  // is explicitly false; cleared by any normal call or an explicit openDialer().
  const [suppressIntercom, setSuppressIntercom] = useState(false);
  // MOBILE-2E P3 — how many mounted surfaces draw every call themselves (see
  // claimCallSurface). The ref is what the accept / dial paths read (they run
  // inside closures created before a re-render); the state re-renders the
  // context so `suppressIntercom` reflects a claim.
  const callSurfaceClaimsRef = useRef(0);
  const [callSurfaceClaims, setCallSurfaceClaims] = useState(0);
  // MOBILE-2E P3 — the call log id (see the context type) and the call it
  // belongs to: a late answer for an earlier call never lands on a newer one.
  const [callLogId, setCallLogId] = useState<number | null>(null);
  const callGenRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const callStartRef = useRef<number>(0);
  const deviceRef = useRef<Device | null>(null);
  const initInFlightRef = useRef(false);
  // True once the voice-token mint answered 401/403 for this user: a wake-up
  // reconnect must not retry a refusal. Reset when the user id changes.
  const tokenForbiddenRef = useRef(false);
  // Suppress repeat toasts for the same Device error within a single broken
  // state — Twilio's SDK can fire 'error' multiple times in quick succession.
  const lastErrorToastKeyRef = useRef<string | null>(null);
  // PHONE-A2 — self-healing. `intentionalTeardownRef` distinguishes "we called
  // destroy()" from "Twilio dropped our registration": both fire 'unregistered',
  // and re-registering a Device we deliberately tore down would resurrect it.
  const intentionalTeardownRef = useRef(false);
  const reregisterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reregisterAttemptRef = useRef(0);

  // PHONE-A1 — the single entry point into the ring state machine. Every ring
  // transition goes through here: the Device's 'incoming', the WS event, a
  // button click, the ring timeout. It runs the pure reducer, mirrors the
  // result into the ref the SDK closures read, and surfaces the reducer's one
  // user-facing message. Returns the intent that just became actionable (if
  // any) so the caller can perform the SDK side effect exactly once.
  const dispatchRing = useCallback((action: IncomingCallAction): RingIntent | null => {
    const { state: next, applyIntent, toast: toastMessage } = incomingCallReducer(ringStateRef.current, action);
    ringStateRef.current = next;
    setRingState(next);
    if (toastMessage) toast.showToast(toastMessage, 'info');
    return applyIntent;
  }, [toast]);

  const initDevice = useCallback(async () => {
    if (!user) return;
    // POST /communications/voice/token is gated tab:comms — for roles without
    // it (e.g. Marketing) the mint can only 403, so skip the init entirely
    // instead of red-logging a guaranteed failure on every app load.
    if (!hasTab('comms')) {
      setConfigured(false);
      setConnectionState('disconnected');
      logDeviceEvent('init_skipped', { reason: 'role lacks tab:comms' });
      return;
    }
    if (initInFlightRef.current) return;
    initInFlightRef.current = true;
    setConnectionState('connecting');
    // T3b.2 — Lifecycle logging. Structured, greppable prefix `[Twilio Device]`
    // so the cold-start question ("did register fire before the first inbound
    // call arrived?") becomes a console scan instead of guesswork.
    logDeviceEvent('initDevice_called', { userId: user.id });
    try {
      const res = await communicationsApi.getVoiceToken();
      if (!res.success) {
        setConfigured(false);
        setConnectionState('disconnected');
        logDeviceEvent('init_skipped', { reason: 'voice token mint failed' });
        return;
      }
      setConfigured(true);

      // Perf 3g: the SDK's runtime enters the page HERE — post-token, comms
      // roles only — instead of in the eager bundle for everyone.
      const { Device: DeviceCtor, Call: CallCtor } = await import('@twilio/voice-sdk');
      const newDevice = new DeviceCtor(res.data.token, {
        logLevel: 'warn',
        codecPreferences: [CallCtor.Codec.Opus, CallCtor.Codec.PCMU],
        // PHONE-A2 — three options that were all sitting on their defaults.
        //
        // edge: 'roaming' is what the SDK already defaults to — the SDK picks
        // the lowest-latency Twilio edge and can fail over to another one. It
        // is stated explicitly because "unset" reads as "nobody decided", and
        // the next person debugging a regional outage needs to know this was a
        // choice and where to change it.
        edge: 'roaming',
        // The default is 10 SECONDS against a token with a 3600-second TTL
        // (twilioService.generateVoiceToken). That means tokenWillExpire fires
        // ten seconds before the token dies — and if that single refresh fails
        // (a blip, a sleeping tab, a redeploy) the Device is simply unusable
        // from then on. Two minutes gives the retry ladder below room to
        // actually work.
        tokenRefreshMs: 120000,
        // Warn before navigating away mid-call. Hanging up on a customer by
        // closing a tab is a real cost; a browser confirm is not.
        closeProtection: true,
      });
      logDeviceEvent('device_constructed', { tokenLength: res.data.token?.length });

      newDevice.on('registered', () => {
        logDeviceEvent('registered');
        setIsReady(true);
        setConnectionState('connected');
        // Successful registration resets the toast suppression key so a future
        // disconnect surfaces a fresh toast.
        lastErrorToastKeyRef.current = null;
        // PHONE-A2 — a successful registration ends the re-register ladder.
        reregisterAttemptRef.current = 0;
        if (reregisterTimerRef.current) {
          clearTimeout(reregisterTimerRef.current);
          reregisterTimerRef.current = null;
        }
      });
      newDevice.on('registering', () => {
        logDeviceEvent('registering');
        setConnectionState((prev) => (prev === 'connected' ? prev : 'connecting'));
      });
      newDevice.on('unregistered', () => {
        logDeviceEvent('unregistered');
        setIsReady(false);
        setConnectionState((prev) => (prev === 'error' ? prev : 'disconnected'));

        // PHONE-A2 (V-03) — RE-REGISTER. This handler used to end here, so a
        // Device that lost its registration for any reason stayed unregistered
        // for the rest of the session: no ring, no error, no indication that
        // anything had happened. Twilio does not re-register on its own.
        //
        // Not after a destroy() we asked for — that would resurrect a Device
        // the app deliberately tore down (an impersonation swap, an unmount).
        if (intentionalTeardownRef.current) return;
        // Nor for a Device that is no longer the current one: initDevice can
        // build a replacement, and the old one's late 'unregistered' must not
        // drag the new one's state around.
        if (deviceRef.current && deviceRef.current !== newDevice) return;

        const attempt = reregisterAttemptRef.current;
        // 1s, 3s, 10s, 30s, then every 60s. Fast enough that a blip is
        // invisible; slow enough that a genuinely dead credential does not
        // become a retry storm for the rest of the working day.
        const ladder = [1000, 3000, 10000, 30000];
        const delay = ladder[attempt] ?? 60000;
        reregisterAttemptRef.current = attempt + 1;
        logDeviceEvent('reregister_scheduled', { attempt: attempt + 1, delayMs: delay });

        if (reregisterTimerRef.current) clearTimeout(reregisterTimerRef.current);
        reregisterTimerRef.current = setTimeout(() => {
          reregisterTimerRef.current = null;
          if (intentionalTeardownRef.current) return;
          if (deviceRef.current !== newDevice) return;
          logDeviceEvent('reregister_attempt', { attempt: attempt + 1 });
          newDevice.register().catch((err: any) => {
            // A failed register does NOT fire 'unregistered' again, so the
            // ladder would stall here without its own rescheduling. Left
            // deliberately as a catch that re-arms rather than a silent one.
            logDeviceEvent('reregister_failed', { attempt: attempt + 1, message: err?.message || String(err) }, 'error');
            const next = reregisterAttemptRef.current;
            const nextDelay = ladder[next] ?? 60000;
            reregisterAttemptRef.current = next + 1;
            if (reregisterTimerRef.current) clearTimeout(reregisterTimerRef.current);
            reregisterTimerRef.current = setTimeout(() => {
              reregisterTimerRef.current = null;
              if (!intentionalTeardownRef.current && deviceRef.current === newDevice) {
                newDevice.register().catch(() => { /* the ladder continues on the next 'unregistered' */ });
              }
            }, nextDelay);
          });
        }, delay);
      });
      newDevice.on('error', (err) => {
        const message = err?.message || 'Twilio device error';
        const code = (err as any)?.code ?? '';
        logDeviceEvent('error', { code, message }, 'error');
        const toastKey = `${code}:${message}`;
        setError(message);
        setConnectionState('error');
        setIsReady(false);
        if (lastErrorToastKeyRef.current !== toastKey) {
          lastErrorToastKeyRef.current = toastKey;
          toast.showToast(`Phone connection lost: ${message}. Re-test in Settings → Phones.`, 'error');
        }
      });

      newDevice.on('incoming', (call: Call) => {
        const raw = ((call as any)?.parameters || {}) as { From?: string; To?: string; CallSid?: string };
        // BULK-1 WP-Calls — the ring TwiML sends the PARENT sid as a custom
        // parameter (twilioService.generateInboundRingTwiml), so this invite
        // and the WS event match by sid, not only by phone number. The SDK
        // hands custom parameters over as a Map.
        let parentSid: string | undefined;
        try {
          const custom = (call as any)?.customParameters;
          const v = custom && typeof custom.get === 'function' ? custom.get('parentCallSid') : undefined;
          parentSid = typeof v === 'string' && v ? v : undefined;
        } catch { /* an SDK without custom parameters: match by number, as before */ }
        const params = { ...raw, ParentCallSid: parentSid };
        logDeviceEvent('incoming', { callSid: params.CallSid, parentCallSid: parentSid, from: params.From, to: params.To });
        // Park the Call SDK object so Accept/Decline can act on it.
        incomingCallObjRef.current = call;
        const callSid = params.CallSid;

        // PHONE-A1 (V-01) — THIS is now the ring trigger. Previously this
        // handler only parked the object and wired listeners: the banner and
        // the ring tone came exclusively from the WS event, so a dropped socket
        // meant a silent phone with Twilio ringing an invite nobody could see.
        // The reducer raises a banner from `call.parameters` when nothing is on
        // screen, and merely records the child sid when the WS event already
        // put one up (which is still the normal order — the server publishes
        // before it returns TwiML).
        const pending = dispatchRing({ type: 'device_incoming', params, nowMs: Date.now(), ringMs: DEFAULT_RING_MS });

        // PHONE-A1 (V-02) — a click that landed before this invite is applied
        // NOW, against the real Call object. applyRingIntentRef is a ref
        // because this closure is created once, inside initDevice, and would
        // otherwise capture the first render's accept/decline forever.
        if (pending) {
          logCallEvent('deferred_intent_applied', callSid, { intent: pending });
          applyRingIntentRef.current?.(pending, call);
        }
        // T5a — Inbound Call lifecycle listeners now also transition callState
        // and tear down activeCall + outboundCallContext on disconnect/cancel/
        // reject. Previously only logged, so callState stayed at 'ringing'
        // after Accept and never returned to 'idle' after hangup — the
        // active-call view stuck around indefinitely.
        call.on('accept', () => {
          logCallEvent('accept', callSid);
          setCallState('open');
        });
        call.on('cancel', () => {
          logCallEvent('cancel', callSid);
          // T5b — Multi-session ring fix. When window A accepts the call, Twilio
          // signals window B's Device by firing this 'cancel' event. Previously
          // we cleared callState/activeCall/outboundCallContext but NOT the
          // banner state (`incomingCall`) or its auto-dismiss timer, so window
          // B kept ringing (banner + Web Audio ring tone) until the 25s
          // ringExpiresAt timer fired. The `phone.incoming_call_resolved` WS
          // event only fires when the call ENDS (dial-status webhook), not
          // when it's accepted — so neither window receives a resolved event
          // during the active call. clearIncomingCall() handles the ref + the
          // banner state + the timer in one call.
          clearIncomingCall();
          setCallState('idle');
          setActiveCall(null);
          setOutboundCallContext(null);
        });
        call.on('disconnect', () => {
          logCallEvent('disconnect', callSid);
          incomingCallObjRef.current = null;
          setCallState('idle');
          setActiveCall(null);
          setIsMuted(false);
          setOutboundCallContext(null);
        });
        call.on('reject', () => {
          logCallEvent('reject', callSid);
          incomingCallObjRef.current = null;
          setCallState('idle');
          setActiveCall(null);
          setOutboundCallContext(null);
        });
      });

      // PHONE-A2 (V-10) — the token refresh gets THREE chances, not one.
      //
      // This handler used to make a single fetch and give up on any failure —
      // logging it and doing nothing else. With the SDK's default
      // tokenRefreshMs of 10 seconds against a 3600-second token, that one shot
      // landed ten seconds before the token died, so a single transient failure
      // (a redeploy, a sleeping tab, a flaky second) left the Device holding an
      // expired credential for the rest of the session — silently. The window
      // is now two minutes (see the Device options above) and this ladder uses
      // it: 2s, then 5s, then 10s.
      newDevice.on('tokenWillExpire', async () => {
        logDeviceEvent('tokenWillExpire');
        const delays = [0, 2000, 5000, 10000];
        for (let attempt = 0; attempt < delays.length; attempt++) {
          if (deviceRef.current && deviceRef.current !== newDevice) return; // superseded
          if (delays[attempt] > 0) await new Promise((r) => setTimeout(r, delays[attempt]));
          try {
            const refreshRes = await communicationsApi.getVoiceToken();
            if (refreshRes.success) {
              newDevice.updateToken(refreshRes.data.token);
              logDeviceEvent('token_refreshed', { attempt: attempt + 1 });
              return;
            }
            // success:false is the "Twilio not configured" state, not a blip —
            // retrying cannot fix it, so stop rather than burn the ladder.
            logDeviceEvent('token_refresh_unconfigured', { attempt: attempt + 1 }, 'error');
            return;
          } catch (err: any) {
            logDeviceEvent('token_refresh_failed', {
              attempt: attempt + 1,
              of: delays.length,
              message: err?.message || String(err),
            }, 'error');
          }
        }
        // Out of attempts. Say so, rather than letting the phone die quietly —
        // the status pill in the dialer reads this state.
        logDeviceEvent('token_refresh_exhausted', undefined, 'error');
        setConnectionState('error');
        setIsReady(false);
        setError('The phone lost its connection and could not renew it.');
      });

      logDeviceEvent('register_called');
      // The new Device is the current one BEFORE register() resolves: the
      // 'unregistered' and 'registering' handlers above compare against
      // deviceRef, and a registration that fails or flaps mid-handshake would
      // otherwise be ignored as belonging to a superseded Device.
      const previous = deviceRef.current;
      deviceRef.current = newDevice;
      intentionalTeardownRef.current = false;
      reregisterAttemptRef.current = 0;
      await newDevice.register();
      logDeviceEvent('register_resolved');
      // Clean up any previous device that may still be mounted (HMR / reconnect).
      if (previous && previous !== newDevice) {
        try { previous.destroy(); } catch { /* ignore */ }
      }
      setDevice(newDevice);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403 || status === 401) {
        // A refused token mint is a STATE (this identity may not use the
        // phone here — e.g. the read-only QA agent, or a tier/role gate), not
        // a transient failure. Retrying it on every tab focus produced a red
        // "[Twilio Device] init_failed" every few seconds for the whole
        // session (2026-09-02 staging sweep). Record it once, at info level,
        // and let the wake-up reconnect skip it until the user changes.
        tokenForbiddenRef.current = true;
        logDeviceEvent('init_skipped', { reason: 'voice token refused', status });
        setConfigured(false);
        setConnectionState('disconnected');
        return;
      }
      logDeviceEvent('init_failed', { message: err?.message || String(err) }, 'error');
      setConfigured(false);
      setConnectionState('disconnected');
    } finally {
      initInFlightRef.current = false;
    }
    // `clearIncomingCall` and `dispatchRing` are declared BELOW this
    // useCallback. Both are stable (useCallback with [] / [toast] deps) and are
    // resolved through the closure at call time, not at definition time, so
    // omitting them is safe — and listing them would rebuild the Device on
    // every toast-context change. The directive sits on the line immediately
    // before the dependency array on purpose: with the explanation above it, an
    // `eslint-disable-next-line` pointed at a comment and suppressed nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasTab, toast]);

  const reconnect = useCallback(async () => {
    // Idempotent: no-op if already connected or connecting.
    if (connectionState === 'connected' || connectionState === 'connecting') return;
    // The token mint was refused for this identity — a wake-up cannot change
    // that, and retrying only re-logs the refusal. Cleared when the user changes.
    if (tokenForbiddenRef.current) return;
    if (deviceRef.current) {
      // PHONE-A2 — flag the teardown as ours so the 'unregistered' handler's
      // re-register ladder does not fight the fresh Device initDevice is about
      // to build. Cleared again inside initDevice.
      intentionalTeardownRef.current = true;
      if (reregisterTimerRef.current) {
        clearTimeout(reregisterTimerRef.current);
        reregisterTimerRef.current = null;
      }
      try { deviceRef.current.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
      setDevice(null);
    }
    setError(null);
    lastErrorToastKeyRef.current = null;
    await initDevice();
  }, [connectionState, initDevice]);

  const testConnection = useCallback(async (): Promise<TwilioTestConnectionResult> => {
    try {
      const response = await api.post('/communications/test-connection');
      const body = response.data?.data || response.data;
      return body as TwilioTestConnectionResult;
    } catch (err: any) {
      return { ok: false, error: err?.response?.data?.error || err?.message || 'Connection test failed' };
    }
  }, []);

  // T3.3 / T3.4 — Incoming-call lifecycle helpers. All clear the ref + state
  // + auto-dismiss timer together so the banner never lingers.
  const clearIncomingCall = useCallback(() => {
    ringStateRef.current = EMPTY_RING_STATE;
    setRingState(EMPTY_RING_STATE);
    incomingCallObjRef.current = null;
    if (incomingDismissTimerRef.current) {
      clearTimeout(incomingDismissTimerRef.current);
      incomingDismissTimerRef.current = null;
    }
  }, []);

  /**
   * PHONE-A1 — the accept EFFECT, separated from the accept CLICK.
   *
   * V-02 was that these two were the same thing: the click set callState to
   * 'open', opened the dialer and cleared the banner whether or not
   * `call.accept()` had run, so answering before the SDK invite arrived showed
   * a live call with nobody on it while the real leg rang on to voicemail. Now
   * every one of those state changes happens HERE, downstream of an accept()
   * that actually executed — whether that is on the click (the Call was
   * already parked) or moments later when the invite lands.
   */
  const performAccept = useCallback((call: Call, banner: IncomingCallBannerData | null) => {
    // T5a — the by-sid lookup needs the PARENT sid. Twilio's <Dial><Client>
    // creates a CHILD leg and the SDK reports that child in
    // call.parameters.CallSid, but comm_call_logs was keyed on the parent at
    // routing time, so the child 404s. The reducer keeps both.
    const lookupSid = banner?.parentCallSid || banner?.callSid || (call as any)?.parameters?.CallSid;

    try {
      call.accept();
    } catch (err) {
      // NOT a silent catch any more. Before, accept() throwing left the UI
      // claiming an open call, because the state changes ran regardless.
      console.error('[Twilio Call] accept_failed at', new Date().toISOString(), '|', err);
      toast.showToast('Could not answer that call. It may have already ended.', 'error');
      setCallState('idle');
      clearIncomingCall();
      return;
    }

    setActiveCall(call);
    // MOBILE-2E P3 — a new call: its log id arrives with the by-sid lookup below.
    const gen = ++callGenRef.current;
    setCallLogId(null);
    // 'connecting', not 'open'. accept() succeeded, but the media path is not
    // up until the SDK fires its own 'accept' event — and the listener wired in
    // Device.on('incoming') promotes it to 'open' when it does. Claiming 'open'
    // here is the same lie V-02 was made of, just a shorter one.
    setCallState('connecting');

    // T5.1 — Surface the active-call view for inbound calls too. Populate
    // outboundCallContext (misnamed historically — it actually covers ANY
    // active call; renaming is post-T6 cleanup) so DialerActiveCall's
    // Notes + Outcome panels light up. callId fetched async via the by-sid
    // lookup route; the surface renders immediately with the caller name.
    if (banner) {
      setOutboundCallContext({
        contactId: undefined,
        name: banner.fromName || undefined,
        phoneNumber: banner.fromNumber,
      });
      // MOBILE-2E P3 — not on the phone shell: its call layer draws the call,
      // and the desktop DialerIntercom must not open over the phone app.
      if (callSurfaceClaimsRef.current === 0) {
        setDialerSeed({
          initialNumber: banner.fromNumber,
          contactName: banner.fromName || undefined,
        });
        setIsDialerOpen(true);
      }
    }

    // PHONE-A5 (V-09) — "I've got it." The only other signal that this call was
    // answered is Twilio's dial-status webhook, which fires when the call ENDS,
    // so on a shared line every colleague's browser kept ringing for the whole
    // length of the conversation. Fire-and-forget on purpose: the webhook is
    // still the authoritative resolution and this is only ever earlier, so a
    // failure here costs the old behaviour, never the call.
    if (lookupSid) {
      api.post(`/communications/calls/${encodeURIComponent(lookupSid)}/answered`)
        .catch(() => { /* best-effort; dial-status still resolves the ring */ });

      api.get(`/communications/calls/by-sid/${encodeURIComponent(lookupSid)}`)
        .then((r) => {
          const log = r.data?.data;
          if (!log) return;
          // MOBILE-2E P3 — kept past the hang-up, for the wrap-up's outcome.
          if (callGenRef.current === gen && log.id) setCallLogId(Number(log.id));
          setOutboundCallContext((prev) => prev ? {
            ...prev,
            callId: log.id,
            contactId: log.contact_id || prev.contactId,
          } : prev);
        })
        .catch(() => { /* best-effort; Notes/Outcome inline message handles the no-callId path */ });
    }

    clearIncomingCall();
  }, [clearIncomingCall, toast]);

  /** PHONE-A1 — the reject effect. Decline and send-to-voicemail are the same
   *  operation to the browser (see sendIncomingCallToVoicemail below). */
  const performReject = useCallback((call: Call) => {
    try { call.reject(); } catch (err) { console.warn('[Twilio Call] reject_failed at', new Date().toISOString(), '|', err); }
    setCallState('idle');
    clearIncomingCall();
  }, [clearIncomingCall]);

  // Registered for the SDK's 'incoming' closure, which is created once inside
  // initDevice and cannot see later renders' callbacks.
  useEffect(() => {
    applyRingIntentRef.current = (intent: RingIntent, call: Call) => {
      if (intent === 'accept') performAccept(call, ringStateRef.current.banner);
      else performReject(call);
    };
    return () => { applyRingIntentRef.current = null; };
  }, [performAccept, performReject]);

  const acceptIncomingCall = useCallback(() => {
    const call = incomingCallObjRef.current;
    const banner = ringStateRef.current.banner;
    logCallEvent('user_accept_clicked', banner?.parentCallSid || banner?.callSid, { hasCallObject: !!call });
    // The reducer decides: act now, or remember the click. There is no third
    // branch where the UI pretends — that was V-02.
    const intent = dispatchRing({ type: 'user_intent', intent: 'accept', hasCallObject: !!call });
    if (intent && call) performAccept(call, banner);
  }, [dispatchRing, performAccept]);

  const declineIncomingCall = useCallback(() => {
    const call = incomingCallObjRef.current;
    const banner = ringStateRef.current.banner;
    logCallEvent('user_decline_clicked', banner?.parentCallSid || banner?.callSid, { hasCallObject: !!call });
    const intent = dispatchRing({ type: 'user_intent', intent: 'decline', hasCallObject: !!call });
    if (intent && call) performReject(call);
  }, [dispatchRing, performReject]);

  // Send-to-voicemail and decline are functionally identical from the
  // browser's side: both reject the Twilio invitation. The TwiML dial-action
  // callback on the server side observes the rejection as 'canceled' and
  // routes to voicemail. Distinct method kept so the UI can tell the two
  // actions apart and future tickets can add explicit server signaling.
  const sendIncomingCallToVoicemail = useCallback(() => {
    const call = incomingCallObjRef.current;
    const banner = ringStateRef.current.banner;
    logCallEvent('user_voicemail_clicked', banner?.parentCallSid || banner?.callSid, { hasCallObject: !!call });
    const intent = dispatchRing({ type: 'user_intent', intent: 'voicemail', hasCallObject: !!call });
    if (intent && call) performReject(call);
  }, [dispatchRing, performReject]);

  // WS event handler. Lights up the banner on incoming_call and clears it on
  // incoming_call_resolved (covers the multi-session "the other tab took it"
  // case per T3.4).
  const handleInboxEvent = useCallback((event: InboxEvent) => {
    if (event.type === 'phone.incoming_call') {
      logCallEvent('ws_incoming_received', event.callSid, { fromNumber: event.fromNumber });
      // PHONE-A1 — MERGE, don't replace. If the Device announced this ring
      // first (a slow or reconnecting socket) the banner is already up, and
      // overwriting it would restart the ring, drop a pending click, and lose
      // the child sid. The reducer folds in what only the server knows: the
      // caller's NAME, the assigned user, and the PARENT CallSid the by-sid
      // lookup needs. When nothing is on screen this is exactly today's
      // behaviour.
      dispatchRing({ type: 'ws_incoming', event, nowMs: Date.now() });
    } else if (event.type === 'phone.incoming_call_resolved') {
      logCallEvent('ws_resolved_received', event.callSid, { status: event.status });
      clearIncomingCall();
    }
  }, [clearIncomingCall, dispatchRing]);

  useInboxSocket(handleInboxEvent);

  // PHONE-A1 — the ring's own deadline, armed from whichever channel announced
  // it. A WS-announced ring uses the server's `ringExpiresAt`; a
  // Device-announced one assumes the same window the server uses
  // (RING_TIMEOUT_SECONDS). Re-arms whenever the deadline changes, which is
  // what lets a WS event that arrives second correct a device-born estimate.
  //
  // The timeout goes through the reducer rather than clearing directly, because
  // a click still pending at the deadline means the user DID answer and the
  // invite never came — and that must be said out loud, not swallowed.
  useEffect(() => {
    if (incomingDismissTimerRef.current) {
      clearTimeout(incomingDismissTimerRef.current);
      incomingDismissTimerRef.current = null;
    }
    const banner = ringState.banner;
    if (!banner) return undefined;
    const expiresMs = Date.parse(banner.ringExpiresAt);
    const dismissMs = Number.isFinite(expiresMs) ? Math.max(0, expiresMs - Date.now()) : DEFAULT_RING_MS;
    incomingDismissTimerRef.current = setTimeout(() => {
      logCallEvent('ring_timeout_dismiss', banner.callSid, { hadPendingClick: !!ringStateRef.current.pendingIntent });
      dispatchRing({ type: 'ring_timeout' });
      incomingCallObjRef.current = null;
    }, dismissMs);
    return () => {
      if (incomingDismissTimerRef.current) {
        clearTimeout(incomingDismissTimerRef.current);
        incomingDismissTimerRef.current = null;
      }
    };
  }, [ringState.banner, dispatchRing]);

  // ── PHONE-A5 (V-07) — the ring that reaches a person in another window ──
  //
  // The account-level toggle (`comms.call_incoming_desktop` in Settings →
  // Notifications) is read ONCE, and ONLY for a browser that has already been
  // granted OS notification permission. Everyone else — the large majority —
  // makes no request at all, because for them the answer cannot change the
  // outcome: without the browser permission there is nothing to suppress.
  //
  // It defaults to true so a failed read never silences the phone. The browser
  // permission is itself an explicit per-device opt-in made from a button in
  // Settings, so "granted but the preference read failed" is a user who asked
  // for this and should get it.
  const [desktopAlertPref, setDesktopAlertPref] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return;
    if (window.Notification.permission !== 'granted') return;
    let cancelled = false;
    notificationsCenterApi.getPreferences()
      .then((prefs) => {
        if (cancelled) return;
        const evt = prefs.categories
          .flatMap((c) => c.events)
          .find((e) => e.key === 'comms.call_incoming_desktop');
        if (evt) setDesktopAlertPref(evt.effective);
      })
      .catch(() => { /* keep the default; see above */ });
    return () => { cancelled = true; };
  }, []);

  // Memoized on the PRIMITIVES, not on `ringState.banner`: the reducer returns
  // a new banner object whenever any field changes (a pending click, the WS
  // enrichment), and an unmemoized object here would close and re-raise the OS
  // notification on every one of those renders.
  const desktopRingBanner = useMemo(() => {
    const b = ringState.banner;
    return b ? { callSid: b.callSid, fromNumber: b.fromNumber, fromName: b.fromName ?? null } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringState.banner?.callSid, ringState.banner?.fromNumber, ringState.banner?.fromName]);

  const desktopRingOpts = useMemo(
    () => ({ preferenceEnabled: desktopAlertPref, onAccept: acceptIncomingCall }),
    [desktopAlertPref, acceptIncomingCall],
  );
  useDesktopRingNotification(desktopRingBanner, desktopRingOpts);

  // T2.3 — load the user's lines to power the From-picker.
  // 2026-07 fix: reads GET /communications/lines/mine (tab:comms; the union of
  // all three assignment stores) instead of GET /phones/assignments/me, which
  // sat behind tab:phones AND read only user_phone_assignments — either gap
  // silently produced "No lines assigned" for users who were plainly assigned
  // in Settings → Communications.
  const refreshAssignments = useCallback(async () => {
    if (!user) {
      setAvailableFromNumbers([]);
      return;
    }
    // GET /communications/lines/mine admits tab:comms OR tab:phones
    // (requireAnyTab) — mirror that gate so roles with neither don't fire a
    // guaranteed-403 fetch on every load.
    if (!hasTab('comms') && !hasTab('phones')) {
      setAvailableFromNumbers([]);
      return;
    }
    setFromNumbersLoading(true);
    try {
      const res = await conversationsApi.myLines();
      const rows = res.data || [];
      const options: FromNumberOption[] = rows
        .filter((l) => l.capabilities_voice)
        .map((l) => ({
          phoneNumberId: l.id,
          phoneNumber: l.phone_number,
          friendlyName: l.friendly_name,
          isDefault: !!l.is_default,
        }));
      options.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      setAvailableFromNumbers(options);
    } catch (err) {
      // Best-effort; the From-picker will fall back to its empty state.
      setAvailableFromNumbers([]);
    } finally {
      setFromNumbersLoading(false);
    }
  }, [user, hasTab]);

  const defaultFromNumber = availableFromNumbers.find((o) => o.isDefault) || availableFromNumbers[0] || null;

  // PHONE-A2 (V-17) — keyed on user?.id, NOT on the `user` OBJECT.
  //
  // AuthContext mints a new user object whenever anything about the session
  // changes — most visibly a role impersonation, but also a permissions refresh
  // or a profile save. Every one of those re-ran this effect, which DESTROYED
  // the Twilio Device. Destroying a Device drops any call on it, so an admin
  // switching role view mid-call hung up on whoever they were talking to, and
  // even without a call it produced a needless unregister/register cycle during
  // which the phone could not ring. The id is what actually decides which
  // Twilio identity the token is minted for; nothing else here depends on the
  // rest of the object.
  const userId = user?.id;
  useEffect(() => {
    tokenForbiddenRef.current = false;
    initDevice();
    refreshAssignments();
    return () => {
      if (deviceRef.current) {
        intentionalTeardownRef.current = true;
        if (reregisterTimerRef.current) {
          clearTimeout(reregisterTimerRef.current);
          reregisterTimerRef.current = null;
        }
        try { deviceRef.current.destroy(); } catch { /* ignore */ }
        deviceRef.current = null;
      }
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // PHONE-A2 (V-03) — WAKE UP AND CHECK. `connectionState` and `reconnect()`
  // have existed on this context since T3 and had ZERO consumers anywhere in
  // frontend/src: the app knew its phone was broken and never acted on it, and
  // never told anyone. The commonest way to arrive here is a laptop lid: the
  // machine sleeps, the signalling socket dies, and nothing re-establishes it
  // until the next full page load. `reconnect()` is already a no-op when the
  // Device is connected or connecting, so this can fire freely.
  useEffect(() => {
    const wake = () => {
      if (connectionState === 'error' || connectionState === 'disconnected') {
        logDeviceEvent('wake_reconnect', { connectionState });
        void reconnect();
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') wake(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', wake);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', wake);
    };
  }, [connectionState, reconnect]);

  useEffect(() => {
    if (callState === 'open') {
      callStartRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (callState === 'idle') setCallDuration(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [callState]);

  const makeCall = useCallback(async (toNumber: string, contactId?: number, clientCardId?: string): Promise<number | undefined> => {
    if (!device || !isReady) {
      setError('Phone system not ready. Check Twilio configuration in Settings.');
      return undefined;
    }

    try {
      setError(null);
      setCallState('connecting');

      const res = await communicationsApi.initiateCall({ to_number: toNumber, contact_id: contactId, client_card_id: clientCardId });
      const callId: number | undefined = res?.data?.call_id;

      const call = await device.connect({
        params: { To: toNumber },
      });
      const outboundCallSid = (call as any)?.parameters?.CallSid;
      logCallEvent('outbound_connect_resolved', outboundCallSid, { toEnding: toNumber.slice(-4) });

      call.on('accept', () => {
        logCallEvent('accept', outboundCallSid);
        setCallState('open');
      });
      call.on('disconnect', () => {
        logCallEvent('disconnect', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
        setIsMuted(false);
      });
      call.on('cancel', () => {
        logCallEvent('cancel', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
      });
      call.on('reject', () => {
        logCallEvent('reject', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
      });
      call.on('error', (err: any) => {
        logCallEvent('error', outboundCallSid, { message: err?.message });
        setError(err.message || 'Call error');
        setCallState('idle');
        setActiveCall(null);
      });

      setActiveCall(call);
      setCallState('ringing');
      return callId;
    } catch (err: any) {
      setError(err.message || 'Failed to place call');
      setCallState('idle');
      return undefined;
    }
  }, [device, isReady]);

  // T4.1 — Canonical outbound entry point. Normalizes the phone, picks a
  // From-number (opts.fromNumber > defaultFromNumber from T2.3), creates the
  // call-log row via the existing /calls/outbound route (FUB auto-log on
  // initiate), opens the dialer overlay so the active-call surface surfaces
  // immediately, then connects through the Twilio Device. The makeCall path
  // above stays for T2/T3-era consumers; new call sites use this.
  const startCall = useCallback(async (
    phoneNumber: string,
    contactContext?: OutboundCallContext,
    opts?: StartCallOptions,
  ): Promise<number | undefined> => {
    const normalized = normalizeToE164(phoneNumber);
    if (!normalized) {
      setError('Phone number is invalid.');
      return undefined;
    }

    // T4a.1 — Set the active-call context + connecting state IMMEDIATELY,
    // before any device-readiness check. This guarantees the dialer surfaces
    // in active-call mode the instant the user clicks (Aircall / FUB pattern)
    // regardless of where in the Device lifecycle we are. If the Device
    // isn't ready, the active-call view shows the error state inline rather
    // than dropping the user on the dialer entry screen — which was the
    // T4a.1 / Cowork TC-C3 failure mode.
    const stagedContext: OutboundCallContext = {
      ...(contactContext || {}),
      phoneNumber: normalized,
    };
    setOutboundCallContext(stagedContext);
    setCallState('connecting');
    // MOBILE-2E P3 — a new call: its log id arrives with the initiate route.
    const gen = ++callGenRef.current;
    setCallLogId(null);
    // A call that opts out of the auto dialer is driving its own in-call UI —
    // keep the global intercom hidden for its whole duration (isInCall alone
    // would otherwise surface it). A normal call re-enables the intercom.
    setSuppressIntercom(opts?.autoOpenDialer === false);
    // MOBILE-2E P3 — on the phone shell its call layer draws every call: the
    // desktop dialer never opens there, whatever the caller asked.
    if (opts?.autoOpenDialer !== false && callSurfaceClaimsRef.current === 0) {
      setDialerSeed({
        initialNumber: normalized,
        contactId: contactContext?.contactId,
        leadId: contactContext?.leadId,
        contactName: contactContext?.name,
      });
      setIsDialerOpen(true);
    }

    if (!device || !isReady) {
      // PHONE-A2 — SAY SOMETHING, AND TRY TO FIX IT. This branch used to set an
      // `error` string that most dialer surfaces never render, so a click on a
      // dead phone produced a dialer that flickered and closed with no
      // explanation anywhere. The toast is the one surface every dialer shares,
      // and the reconnect is free: reconnect() is a no-op when the Device is
      // already connected or connecting, so the next click usually works.
      setError('Phone system not ready. Check Twilio configuration in Settings.');
      toast.showToast(
        configured
          ? 'The phone is offline. Reconnecting — try again in a moment.'
          : 'Calling isn’t set up yet. Connect Twilio in Settings → Communications.',
        'error',
      );
      if (configured) void reconnect();
      // Roll back the staged active-call state so the user can dismiss the
      // dialer (close button is disabled while in-call); the inline error
      // surface inside DialerActiveCall covers the explanation.
      setCallState('idle');
      setOutboundCallContext(null);
      return undefined;
    }

    try {
      setError(null);
      // outboundCallContext / dialer seed / callState already set above before
      // the device-ready gate. No need to re-set them here.
      logCallEvent('startCall_invoked', undefined, { toEnding: normalized.slice(-4), hasContact: !!contactContext?.contactId });

      // PHONE-A6 — the log write and the dial now run in PARALLEL. The dial used
      // to wait for POST /calls/outbound to come back, so every outbound call
      // carried a full server round-trip of dead air before it started ringing
      // — and if that request was slow the user sat on "connecting" wondering
      // whether the click had registered. Nothing about placing the call needs
      // the log row: the callId is applied to outboundCallContext when it
      // arrives (the Notes/Outcome panels already tolerate a late callId and
      // show their inline "no call id yet" state until then).
      // PHONE-C3 — `deal_id` rides along. `initiateCall`'s declared parameter
      // type lives in the FROZEN services/api.ts and does not name it, but the
      // wrapper passes its argument verbatim to `api.post` with no field
      // whitelisting, and the `as any` below (which predates this ticket)
      // defeats the excess-property check. So the field reaches
      // POST /communications/calls/outbound, which destructures it — the same
      // way `lead_id` has been sent for a year without appearing in that type.
      // DO NOT DELETE `deal_id` as unused: the frozen type is what is stale,
      // not this call site. Widening api.ts would need a frozen-file unlock for
      // one optional property.
      const logPromise = communicationsApi.initiateCall({
        to_number: normalized,
        contact_id: contactContext?.contactId,
        client_card_id: contactContext?.clientCardId,
        from_number: opts?.fromNumber,
        deal_id: contactContext?.dealId,
      } as any).then((initRes: any) => {
        const id: number | undefined = initRes?.data?.call_id;
        if (id) setOutboundCallContext((prev) => prev ? { ...prev, callId: id } : prev);
        // MOBILE-2E P3 — kept past the hang-up, for the wrap-up's outcome.
        if (id && callGenRef.current === gen) setCallLogId(id);
        return id;
      }).catch((err: any) => {
        // A failed LOG must not fail the CALL. Before this, the await meant a
        // 500 here threw straight into the catch below and the call was never
        // placed at all — logging is bookkeeping, dialling is the job.
        //
        // PHONE-B1 — with ONE exception. This route now also evaluates the dial
        // policy, and a refusal here is not a bookkeeping failure: it is the
        // reason the call is about to be stopped. The webhook refuses the call
        // itself (that is the gate that matters, and it speaks the reason down
        // the line), but without this branch the swallow above would drop the
        // only WRITTEN explanation on the floor and the user would get a call
        // that hung up on them for no visible reason. The dial is deliberately
        // NOT awaited on this — that would undo PHONE-A6's parallelism for
        // every call to fix the rare one.
        const refusal = err?.response?.data;
        if (refusal?.code === 'DIAL_POLICY_REFUSED' && refusal?.error) {
          toast.showToast(refusal.error, 'error');
        }
        logCallEvent('outbound_log_failed', undefined, { message: err?.message, code: refusal?.code });
        return undefined;
      });

      // FromNumber, NOT From: Twilio owns `From` on the TwiML App request and
      // sets it to `client:user_<id>`. The server re-checks this against the
      // user's accessible lines before it becomes the caller ID (PHONE-A6).
      const chosenFrom = opts?.fromNumber || defaultFromNumber?.phoneNumber;
      const call = await device.connect({
        params: chosenFrom ? { To: normalized, FromNumber: chosenFrom } : { To: normalized },
      });
      const outboundCallSid = (call as any)?.parameters?.CallSid;
      logCallEvent('outbound_connect_resolved', outboundCallSid, { chosenFrom });

      call.on('accept', () => {
        logCallEvent('accept', outboundCallSid);
        setCallState('open');
      });
      call.on('disconnect', () => {
        logCallEvent('disconnect', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
        setIsMuted(false);
        setOutboundCallContext(null);
      });
      call.on('cancel', () => {
        logCallEvent('cancel', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
        setOutboundCallContext(null);
      });
      call.on('reject', () => {
        logCallEvent('reject', outboundCallSid);
        setCallState('idle');
        setActiveCall(null);
        setOutboundCallContext(null);
      });
      call.on('error', (err: any) => {
        logCallEvent('error', outboundCallSid, { message: err?.message });
        setError(err?.message || 'Call error');
        setCallState('idle');
        setActiveCall(null);
        setOutboundCallContext(null);
      });

      setActiveCall(call);
      setCallState('ringing');
      // The call is LIVE before this line. Awaiting here only settles the
      // return value for callers that use it; the ringing already started.
      return await logPromise;
    } catch (err: any) {
      logCallEvent('startCall_failed', undefined, { message: err?.message }, );
      setError(err?.message || 'Failed to place call');
      setCallState('idle');
      setOutboundCallContext(null);
      return undefined;
    }
  }, [device, isReady, defaultFromNumber, configured, toast, reconnect]);

  // T4 — Global dialer surface. Click-to-call and contact-card buttons call
  // openDialer() to surface the overlay without each call site mounting its
  // own SoftphoneDrawer instance.
  const openDialer = useCallback((opts?: OpenDialerOptions) => {
    setDialerSeed(opts || null);
    setIsDialerOpen(true);
    // Explicitly opening the dialer always un-suppresses the intercom.
    setSuppressIntercom(false);
  }, []);

  const closeDialer = useCallback(() => {
    setIsDialerOpen(false);
    setDialerSeed(null);
  }, []);

  const hangUp = useCallback(() => {
    if (activeCall) {
      activeCall.disconnect();
      setActiveCall(null);
      setIsMuted(false);
    }
    // T4a.1 — Always reset the in-call indicators, even when there's no
    // Twilio Call object yet (the "stuck connecting" path after startCall
    // fires but before device.connect() resolves). Otherwise End Call from
    // that state is a no-op and the user can't dismiss the surface.
    setCallState('idle');
    setOutboundCallContext(null);
  }, [activeCall]);

  const toggleMute = useCallback(() => {
    if (activeCall) {
      const newMuted = !isMuted;
      activeCall.mute(newMuted);
      setIsMuted(newMuted);
    }
  }, [activeCall, isMuted]);

  const sendDtmf = useCallback((digit: string) => {
    if (activeCall) {
      activeCall.sendDigits(digit);
    }
  }, [activeCall]);

  // MOBILE-2E P3 — see the context type. Idempotent release.
  const claimCallSurface = useCallback(() => {
    callSurfaceClaimsRef.current += 1;
    setCallSurfaceClaims(callSurfaceClaimsRef.current);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      callSurfaceClaimsRef.current = Math.max(0, callSurfaceClaimsRef.current - 1);
      setCallSurfaceClaims(callSurfaceClaimsRef.current);
    };
  }, []);
  // A claimed surface keeps the intercom down for every call, whoever placed it.
  const intercomSuppressed = suppressIntercom || callSurfaceClaims > 0;

  // Perf 3d: memoised — this was a raw object literal, so EVERY provider
  // render (including each 1 Hz duration tick during a call) minted a new
  // value and re-rendered all consumers. Every member below is useState or
  // useCallback, so the memo only breaks when something real changes.
  const value = useMemo(() => ({
    device, isReady, activeCall, callState, isMuted,
    makeCall, hangUp, toggleMute, sendDtmf, error, configured,
    connectionState, reconnect, testConnection,
    availableFromNumbers, defaultFromNumber, fromNumbersLoading, refreshAssignments,
    incomingCall, acceptIncomingCall, declineIncomingCall, sendIncomingCallToVoicemail,
    startCall, outboundCallContext,
    isDialerOpen, dialerSeed, openDialer, closeDialer, suppressIntercom: intercomSuppressed,
    claimCallSurface, callLogId,
  }), [
    device, isReady, activeCall, callState, isMuted,
    makeCall, hangUp, toggleMute, sendDtmf, error, configured,
    connectionState, reconnect, testConnection,
    availableFromNumbers, defaultFromNumber, fromNumbersLoading, refreshAssignments,
    incomingCall, acceptIncomingCall, declineIncomingCall, sendIncomingCallToVoicemail,
    startCall, outboundCallContext,
    isDialerOpen, dialerSeed, openDialer, closeDialer, intercomSuppressed,
    claimCallSurface, callLogId,
  ]);

  return (
    <TwilioContext.Provider value={value}>
      <CallDurationContext.Provider value={callDuration}>
      {children}
      {/* T3.3 — Ring banner mounts via createPortal from inside this component so
          we never need to touch the frozen AppShell. The banner self-hides when
          incomingCall is null. */}
      {/* Perf 3g: both are lazy now — fallback null is exactly their idle
          render, so the swap is invisible. */}
      <React.Suspense fallback={null}>
        {/* V-18 — DESKTOP ONLY. On a phone the shell's call layer
            (mobile/v2/phone/MobileCallLayer.tsx — Wave M's CallScreen until
            MOBILE-2E P3) draws its own incoming call from this same
            `incomingCall` state, on every tab, so both were on screen at
            once: two banners, two sets of Answer/Decline buttons, and — because
            each mounts its own useRingTone — two overlapping ring tones beating
            against each other. Same breakpoint App.tsx uses to choose the mobile
            shell (767.95px); a shared constant would have to live in App.tsx,
            which is frozen. */}
        {/* MOBILE-2E P3: and never while a call surface is claimed — the phone
            shell's call layer draws the ring on every tab. */}
        {!isMobileShell && callSurfaceClaims === 0 && <IncomingCallBanner />}
        {/* T4 — Global dialer surface. Opens when openDialer() is called (e.g.
            from PhoneNumberCallButton anywhere in the app). Each existing
            SoftphoneDrawer caller continues to mount its own drawer; this is
            purely additive for click-to-call sites that have no parent mount. */}
        <GlobalDialer />
      </React.Suspense>
      {/* The header phone button now lives INLINE in the top app-bar Toolbar
          (App.tsx), not as a floating element — it opens this same dialer. */}
      </CallDurationContext.Provider>
    </TwilioContext.Provider>
  );
};
